import { useCallback, useEffect, useMemo, useRef } from 'react';
import videojs from 'video.js';
import type Player from 'video.js/dist/types/player';
import 'video.js/dist/video-js.css';
import '@videojs/themes/dist/city/index.css';
import './ChapteredVideoPlayer.css';
import { createRoot, type Root } from 'react-dom/client';
import styled, { ThemeProvider } from 'styled-components';
import { XIcon } from '@phosphor-icons/react';
import { radius } from '@libertify/ui';
import cuepointsPlugin from '../plugins/cuepoints';
import {
  ChaptersOverlayComponent,
  ChapterMarkersComponent,
} from './ChaptersOverlayComponent';
import { KNOWLEDGE_HUB_THEME } from '../knowledgeHubTheme';
import { useScormPlayer, type ScormPlayerConfig } from '../hooks/useScormPlayer';
import {
  DEFAULT_PLAYBACK_RATES,
  type CustomTheme,
  type Cuepoint,
  type Section,
} from '../types';

export interface ChapteredVideoPlayerProps {
  /** Progressive (mp4) video source URL. */
  url: string;
  /** Optional poster image shown before playback. */
  poster?: string;
  /** Chapter list — drives the chapters menu + progress-bar segments. */
  sections?: Section[];
  /**
   * Cuepoints registered on the player. As playback first reaches each
   * one, `onCuepointStart` fires (and a `cuepoints:start` event is
   * triggered). Drives the video↔document scroll sync.
   */
  cuepoints?: Cuepoint[];
  /**
   * Called as playback first reaches a cuepoint — the page uses it to
   * scroll the document to the matching highlight.
   */
  onCuepointStart?: (cuepoint: Cuepoint) => void;
  /** Start playback as soon as the player is ready. */
  autoPlay?: boolean;
  /** Speeds offered by the playback-rate menu. */
  playbackRates?: number[];
  /**
   * Theme for the styled-components subtree (chapters menu fonts/colors).
   * Defaults to the Knowledge Hub theme.
   */
  theme?: CustomTheme;
  /** Called once the underlying Video.js player is ready. */
  onReady?: (player: Player) => void;
  /**
   * When provided, renders a close (×) button over the player. Used by
   * the experience page's expand-on-play UX to collapse back to the
   * poster card.
   */
  onClose?: () => void;
  /** Accessible label for the close button (consumer-translated). */
  closeLabel?: string;
  /**
   * SCORM packaging config. Omit for the normal player; pass `{ enabled:
   * true, … }` when the experience is launched as a SCORM SCO (LMS embed).
   */
  scorm?: ScormPlayerConfig;
  className?: string;
}

/**
 * ChapteredVideoPlayer — standalone Video.js player for the Knowledge
 * Hub experience. The clean counterpart to `PdfDocumentViewer`: it gives
 * consumers the chaptered/cuepoint video experience (chapter menu,
 * progress-bar chapter segments, playback rates, cuepoint events)
 * WITHOUT the `ResponsiveApp` orchestrator's mobile PiP, drag-to-move,
 * double-tap-to-scale and remote SVG-fetch coupling.
 *
 * Stateless re: data — the consumer owns the url/poster/sections/cuepoints
 * (loaded from the publication). This component owns the player chrome.
 *
 * The live Video.js player is handed to the consumer via `onReady` (fired
 * once it's ready) — that's the hook the page will use to drive
 * video↔document sync in a later PR.
 */
export const ChapteredVideoPlayer = ({
  url,
  poster,
  sections = [],
  cuepoints = [],
  autoPlay = false,
  playbackRates = DEFAULT_PLAYBACK_RATES,
  theme = KNOWLEDGE_HUB_THEME,
  onReady,
  onCuepointStart,
  onClose,
  closeLabel,
  scorm,
  className,
}: ChapteredVideoPlayerProps) => {
    const hostRef = useRef<HTMLDivElement>(null);
    const playerRef = useRef<Player | null>(null);

    // Latest cuepoint handler in a ref so the cuepoint-registration effect
    // can stay keyed on `cuepoints` alone — the page passes a fresh inline
    // handler each render, which would otherwise re-register on every
    // re-render.
    const onCuepointStartRef = useRef(onCuepointStart);
    useEffect(() => {
      onCuepointStartRef.current = onCuepointStart;
    });

    // Keep latest sections + theme in refs so the player-init effect
    // doesn't depend on them (re-running it would dispose/recreate the
    // player). The bridges read these on (re)mount. Refs are kept current
    // in an effect (never during render) and re-synced just before each
    // bridge remount below.
    const sectionsRef = useRef(sections);
    const themeRef = useRef(theme);

    // Latest cuepoints in a ref so registration is driven from a single
    // place (the player's `ready` callback + a re-register effect) without
    // the player-init effect depending on the array.
    const cuepointsRef = useRef(cuepoints);
    // Set true once the player fires `ready`; gates the re-register effect
    // so it never runs before the plugin is initialised (the effect can
    // commit before Video.js's async `ready`).
    const readyRef = useRef(false);

    // Single cuepoint-registration path: clear any previous set (removes
    // their timeupdate listeners), (re)init the plugin so `instances` is an
    // empty array, then add the current cuepoints. Called from `ready`
    // (initial) and from the re-register effect (when cuepoints change).
    const registerCuepoints = useCallback((player: Player) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const vjs = player as any;
      vjs.destroyCuepoints?.();
      vjs.cuepoints();
      cuepointsRef.current.forEach((cuepoint, index) => {
        vjs.addCuepoint({
          namespace: 'cuepoints',
          start: cuepoint.start,
          // Open-ended (-1): fire once playback first reaches `start`. A
          // point cue (start === end) would never satisfy the plugin's
          // `currentTime >= start && currentTime < end` check.
          end: -1,
          onStart: () => {
            onCuepointStartRef.current?.(cuepoint);
            player.trigger('cuepoints:start', { index, cuepoint });
          },
          onEnd: () => player.trigger('cuepoints:end', { index, cuepoint }),
        });
      });
    }, []);

    // SCORM tracking + no-seek UI (inert unless `scorm.enabled`). `applyOnReady`
    // is called from the player's `ready` callback below; `scormDisableSeek`
    // drives the control bar (hide the speed menu while the seek lock holds).
    const { disableSeek: scormDisableSeek, applyOnReady: applyScormOnReady } =
      useScormPlayer({ scorm, playerRef, playbackRates });

    // Memoize options on the inputs that genuinely require a fresh player.
    // A url change recreates the player (cleanly disposing the old one);
    // poster/autoplay/rates ride along since they're cheap.
    const options = useMemo(
      () => ({
        autoplay: autoPlay,
        controls: true,
        fill: true,
        responsive: true,
        preload: 'metadata',
        poster,
        experimentalSvgIcons: true,
        playbackRates,
        sources: [{ src: url, type: 'video/mp4' }],
        // Mirror the production player's control bar: skip-10 / skip-10 /
        // playback-rate on the left, centered play/pause, chapters on the
        // right (positioned via ChapteredVideoPlayer.css). No volume,
        // fullscreen, remaining-time or PiP.
        controlBar: {
          fullscreenToggle: false,
          pictureInPictureToggle: false,
          // Hidden while the SCORM seek lock holds (rates let learners skim);
          // restored by useScormPlayer's unlock on completion.
          playbackRateMenuButton: !scormDisableSeek,
          remainingTimeDisplay: false,
          volumePanel: false,
          skipButtons: { forward: 10, backward: 10 },
        },
      }),
      [url, poster, autoPlay, playbackRates, scormDisableSeek],
    );

    // Create + dispose the player inside one effect so React Strict Mode's
    // mount → unmount → remount cycle leaves exactly one live player.
    // dispose() is deferred via queueMicrotask: Video.js synchronously
    // removes DOM nodes that host the bridge React roots, and doing that
    // during React's commit phase throws "Cannot unmount a root while
    // React is rendering".
    useEffect(() => {
      const host = hostRef.current;
      if (!host) return;

      const videoEl = document.createElement('video-js');
      videoEl.setAttribute('playsInline', 'true');
      // Same theme stack as the production viewer player: `city` provides
      // the base skin, `lify` the brand styling (big play button, gradient
      // control bar), `desktop` the rounded corners. The control-bar
      // layout in ChapteredVideoPlayer.css is scoped to these classes.
      videoEl.classList.add('vjs-theme-city', 'vjs-theme-lify', 'vjs-theme-desktop');
      host.appendChild(videoEl);

      if (!videojs.getPlugin('cuepoints')) {
        videojs.registerPlugin('cuepoints', cuepointsPlugin);
      }

      const player = videojs(videoEl, options, () => {
        onReady?.(player);

        // Init + register cuepoints exactly once the player is ready (the
        // re-register effect is gated on this so it can't race ahead).
        readyRef.current = true;
        registerCuepoints(player);

        // Attach SCORM tracking + no-seek skin (inert unless scorm.enabled).
        applyScormOnReady(player);

        // Chapters menu button (control bar) + progress-bar chapter
        // segments (seek bar). Both reuse the existing chapter components
        // via a small React bridge — see LifyReactBridge below.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (player as any).controlBar.addChild('LifyReactBridge', {
          wrapperClass: 'vjs-chapters-overlay',
          themeRef,
          render: (p: Player) => (
            <ChaptersOverlayComponent player={p} sections={sectionsRef.current} />
          ),
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const seekBar = (player as any).controlBar
          ?.getChild('progressControl')
          ?.getChild('seekBar');
        seekBar?.addChild('LifyReactBridge', {
          wrapperClass: 'vjs-chapter-markers',
          themeRef,
          render: (p: Player) => (
            <ChapterMarkersComponent player={p} sections={sectionsRef.current} />
          ),
        });
      });
      playerRef.current = player;

      return () => {
        playerRef.current = null;
        readyRef.current = false;
        if (!player.isDisposed()) {
          queueMicrotask(() => {
            if (!player.isDisposed()) player.dispose();
          });
        }
      };
    }, [options]);

    // Re-register when cuepoints change (e.g. admin's async asset load).
    // The initial registration is done by the `ready` callback; this only
    // re-runs once the player is ready, so it can't race the plugin init.
    useEffect(() => {
      cuepointsRef.current = cuepoints;
      if (playerRef.current && readyRef.current) {
        registerCuepoints(playerRef.current);
      }
    }, [cuepoints, registerCuepoints]);

    // Re-render the chapter bridges when sections or theme change after
    // init. Sync the refs here (in an effect, never during render) so the
    // bridge render thunks — which read the refs — pick up fresh values.
    useEffect(() => {
      sectionsRef.current = sections;
      themeRef.current = theme;
      const player = playerRef.current;
      if (!player) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const cb = (player as any).controlBar;
      cb?.getChild('LifyReactBridge')?.remount?.();
      cb?.getChild('progressControl')
        ?.getChild('seekBar')
        ?.getChild('LifyReactBridge')
        ?.remount?.();
    }, [sections, theme]);

    return (
      <StyledRoot className={className} data-vjs-player>
        <div ref={hostRef} style={{ width: '100%', height: '100%' }} />
        {onClose && (
          <StyledClose
            type="button"
            aria-label={closeLabel}
            onClick={() => {
              // Pause first so audio doesn't keep playing through the
              // shrink animation and then cut off when the player unmounts.
              playerRef.current?.pause();
              onClose();
            }}
          >
            <XIcon size={20} weight="bold" />
          </StyledClose>
        )}
      </StyledRoot>
    );
};

export default ChapteredVideoPlayer;

// ─────────────────────────────────────────────────────────────────────
// Video.js → React bridge
//
// Video.js owns its control-bar DOM; to render React (chapters menu,
// chapter markers) inside it we mount a React root onto a Video.js
// Component's element. One generic bridge replaces the per-feature bridge
// classes the legacy player duplicated. The `render` thunk + `themeRef`
// are passed via options; `remount()` re-renders when sections change.
// ─────────────────────────────────────────────────────────────────────
const VjsComponent = videojs.getComponent('Component');

class LifyReactBridge extends VjsComponent {
  reactRoot: Root | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderFn: (player: any) => React.ReactNode;
  themeRef: { current: CustomTheme };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(player: any, options: any) {
    super(player, options);
    this.renderFn = options.render;
    this.themeRef = options.themeRef ?? { current: KNOWLEDGE_HUB_THEME };
    if (options.wrapperClass) this.addClass(options.wrapperClass);
    this.remount = this.remount.bind(this);

    player.ready(() => queueMicrotask(() => this.remount()));

    this.on('dispose', () => {
      const root = this.reactRoot;
      this.reactRoot = null;
      // Defer unmount outside Video.js's synchronous dispose DOM teardown.
      if (root) setTimeout(() => root.unmount(), 0);
    });
  }

  remount() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((this.player_ as any).isDisposed?.() || !this.el()) return;
    if (!this.reactRoot) this.reactRoot = createRoot(this.el());
    this.reactRoot.render(
      <ThemeProvider theme={this.themeRef.current as never}>
        {this.renderFn(this.player_)}
      </ThemeProvider>,
    );
  }
}

videojs.registerComponent('LifyReactBridge', LifyReactBridge);

// ─── Styled components ───

const StyledRoot = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background: #0f172a;
  overflow: hidden;

  .video-js {
    width: 100%;
    height: 100%;
  }
`;

const StyledClose = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: ${radius.circle};
  cursor: pointer;
  color: #fff;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(6px);
  transition: background 0.15s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.75);
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.7);
    outline-offset: 2px;
  }
`;
